import { useInfiniteQuery } from '@tanstack/react-query';
import { apiGet, operationsClient } from '@/lib/api';
import { contactEndpoints } from '@/lib/api/endpoints';
import { contactKeys } from '@/lib/queryKeys';
import { isApiError, normalizeError } from '@/lib/api/errors';
import type { ContactMediaPage } from '@/types/media';

export interface ContactMediaFilter {
  jobId?: string;
  serviceAddressKey?: string;
  tag?: string;
  /** ISO date (inclusive lower bound on capture time). */
  from?: string;
  /** ISO date (inclusive upper bound on capture time). */
  to?: string;
}

const PAGE_SIZE = 24;

/**
 * NOT-573 — contact media roll-up across all the contact's jobs (and their
 * visits), newest-first, lazily paginated so large histories never trigger an
 * unbounded fetch. Filterable by job, tag and capture-date range.
 */
export function useContactMedia(contactId: string, filter: ContactMediaFilter = {}) {
  const filterParams: Record<string, string> = {};
  if (filter.jobId) filterParams.jobId = filter.jobId;
  if (filter.serviceAddressKey) filterParams.serviceAddressKey = filter.serviceAddressKey;
  if (filter.tag) filterParams.tag = filter.tag;
  if (filter.from) filterParams.from = filter.from;
  if (filter.to) filterParams.to = filter.to;

  return useInfiniteQuery({
    queryKey: contactKeys.media(contactId, filterParams),
    enabled: Boolean(contactId),
    initialPageParam: 0,
    queryFn: async ({ pageParam }) => {
      try {
        return await apiGet<ContactMediaPage>(operationsClient, contactEndpoints.media(contactId), {
          params: { ...filterParams, limit: PAGE_SIZE, offset: pageParam },
        });
      } catch (err) {
        throw isApiError(err) ? err : normalizeError(err);
      }
    },
    getNextPageParam: (lastPage) => {
      const loaded = lastPage.offset + lastPage.items.length;
      return loaded < lastPage.total ? loaded : undefined;
    },
    meta: { suppressToast: true },
  });
}
